import * as React from 'react';
import TextField from '@mui/material/TextField';
import Link from '@mui/material/Link';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import DebugService from '../services/DebugService';
import { useNavigate, useSearchParams } from 'react-router-dom';
import LeftImg from './common/LeftImg';
import { Divider } from '@mui/material';
import GoogleSignUp from './GoogleSignUp';
import Captcha from './common/Captcha';
import { useAuth } from '../state/AuthContext';
import { CONST_ROUTE_PATH } from '../route/RouteConstants';
import {
  CONST_SITE_LABEL,
  MESSAGES,
  ONBOARD_STATUS,
  REGISTRATION_TYPE,
  STORAGE_KEYS,
  navigateToDashboardPostLogin,
  parseJwt,
  validateEmail,
  validatePassword
} from './common/Helper';
import LoginApiService from '../services/LoginApiService';
import { LoadingButton } from '@mui/lab';
import { useScopedLocalStorage } from '../hooks/useScopedLocalStorage';

export default function SignInSide() {
  const navigate = useNavigate()

  const [isInvalidEmail, setIsInvalidEmail] = React.useState(false);
  const [isInvalidPassword, setIsInvalidPassword] = React.useState(false);
  const [errors, setErrors] = React.useState({ email: '', password: '' });
  const [apiErrorMessage, setAPIErrorMessage] = React.useState('');
  const [apiErrorMessageForGoogle, setAPIErrorMessageForGoogle] = React.useState('');
  const [inProgress, setInProgress] = React.useState(false);
  const [isButtonDisabled, setIsButtonDisabled] = React.useState(true);
  const [isCaptchaVerified, setIsCaptchaVerified] = React.useState(false);
  const [captchaError, setCaptchaError] = React.useState('');
  const [showPassword, setShowPassword] = React.useState(false);

  const captchaRef = React.useRef(null);

  const {
    loginWithApi,
    loginWithGoogle,
    setIsOnboarded,
    isOnboarded,
    logout
  } = useAuth();

  const { setScopedItem, getScopedItem } = useScopedLocalStorage()

  const [searchParams] = useSearchParams()

  /**
   * @description Sign In submit handler
   * @param {*} event Submit event
   */
  const handleSubmit = (event) => {
    event.preventDefault();
    setInProgress(true)

    const data = new FormData(event.currentTarget);
    DebugService.log(`SignIn component: `, {
      email: data.get('email'),
      password: data.get('password'),
    });

    const newErrors = {}

    const isValidEmail = validateEmail(data.get('email'))
    if (!isValidEmail) {
      setIsInvalidEmail(true)
      newErrors['email'] = MESSAGES.EMAIL_VALID
      setErrors(newErrors)
    }

    const isValidPassword = validatePassword(data.get('password'))
    if (!isValidPassword) {
      setIsInvalidPassword(true)
      newErrors['password'] = MESSAGES.PASSWORD_VALID
      setErrors(newErrors)
    }

    const captchaInput = (data.get('captchaInput') || '').trim();
    let isCaptchaValid = false;
    if (!captchaInput) {
      setCaptchaError('Please fill captcha');
    } else if (!isCaptchaVerified) {
      setCaptchaError('Captcha mismatch');
    } else {
      isCaptchaValid = true;
    }

    if (isValidEmail && isValidPassword && isCaptchaValid) {
      const requestBody = {
        email: data.get('email'),
        password: data.get('password'),
      }

      LoginApiService.manual(requestBody)
        .then(response => {
          DebugService.log(`Sign in component: ${REGISTRATION_TYPE.MANUAL} Login API response`, response)

          if (response && response['data']) {
            const loginToken = response['data']['loginToken']
            if (loginToken) {
              // Navigate on receiving token
              loginWithApi(loginToken)
              setScopedItem('userID', response['data']['userId'].toString())
              setScopedItem('email', requestBody['email'].toString())
              setIsOnboarded(response['data']['userAccountStatus'] === ONBOARD_STATUS.LINKED)

              navigateToDashboardPostLogin(
                navigate,
                response['data']['userAccountStatus'] === ONBOARD_STATUS.LINKED
              )
            }
          } else if (response && response['error']) {
            const errMessage = response['error']['message']
            if (errMessage) {
              // Display error message to the user
              if (errMessage.toLowerCase().includes('credential')) {
                setAPIErrorMessage('Creds mismatch')
              } else {
                setAPIErrorMessage(errMessage)
              }
            } else {
              DebugService.warn(`Sign in component: response.error not found`, response)
            }
          } else {
            DebugService.warn(`Sign in component: response.data not found`, response)
          }
        })
        .catch(e => {
          DebugService.error(`Sign in component: Error caught ${REGISTRATION_TYPE.MANUAL} while login attempt`, e)
        })
        .finally(() => {
          setInProgress(false)
          captchaRef.current?.refresh()
        })
    } else {
      setInProgress(false)
    }
  };

  /**
   * @description Captcha pass/fail handler
   * @param {boolean} isValid
   */
  const handleCaptchaVerify = (isValid) => {
    setIsCaptchaVerified(isValid)
    if (isValid) {
      setCaptchaError('')
    }
  }

  const handleChange = (e) => {
    const password = document.getElementById('password')?.value ?? ''
    const email = document.getElementById('email')?.value ?? ''

    if (password !== '' && email !== '') {
      setIsButtonDisabled(false)
    }
  }

  /**
   * Google sign up success handler
   * @param {*} data 
  */
  const googleSuccessHandle = (data) => {
    // Reset API error message
    setAPIErrorMessageForGoogle('')
    setInProgress(true)

    const userData = parseJwt(data['credential'])
    const jsonData = data

    if (userData['email'] && userData['email'] !== '') {
      DebugService.log('Sign in component: User data', userData)
      jsonData['user_info'] = userData

      const requestBody = {
        "email": userData['email'],
        "googleLoginToken": 'response.credential',
        "googleJson": JSON.stringify(jsonData)
      }

      // Call registration api is form is valid
      LoginApiService.google(requestBody)
        .then(response => {
          DebugService.log(`Sign in component: ${REGISTRATION_TYPE.GOOGLE} Login API response`, response)

          if (response && response['data']) {
            const loginToken = response['data']['loginToken']
            if (loginToken) {
              // Navigate on receiving token
              loginWithGoogle(loginToken)
              setScopedItem('userID', response['data']['userId'].toString())
              setScopedItem('email', requestBody['email'].toString())
              setIsOnboarded(response['data']['userAccountStatus'] === ONBOARD_STATUS.LINKED)

              navigateToDashboardPostLogin(
                navigate,
                response['data']['userAccountStatus'] === ONBOARD_STATUS.LINKED
              )
            }
          } else if (response && response['error']) {
            const errMessage = response['error']['message']
            if (errMessage) {
              // Display error message to the user
              setAPIErrorMessageForGoogle(errMessage)
            } else {
              DebugService.warn(`Sign in component: response.error not found`, response)
            }
          } else {
            DebugService.warn(`Sign in component: response.data not found`, response)
          }
        })
        .catch(e => {
          DebugService.error(`Sign in component: Error caught ${REGISTRATION_TYPE.GOOGLE} while login attempt`, e)
        })
        .finally(() => setInProgress(false))
    }
  }

  /**
   * Google sign up failure handler
   * @param {*} error 
   */
  const googleFailureHandle = (error) => {
    DebugService.error(`Sign in component: Error caught while google sign in`, error)
  }

  React.useEffect(() => {
    document.title = `${CONST_SITE_LABEL} | Sign In`

    if (searchParams.has('reason') && searchParams.get('reason').toLocaleLowerCase().includes('unauthorize')) {
      logout()
    } else {
      if (getScopedItem(STORAGE_KEYS.LOCAL_USER_DETAILS)) {
        navigateToDashboardPostLogin(navigate, isOnboarded)
      }
    }
  }, [])

  return (
    <Grid container className='sign-in-container'>

      {/* Left Panel */}
      <Grid item xs={12} lg={6}>
        <LeftImg />
      </Grid>

      {/* Right Panel */}
      <Grid item xs={12} lg={6} className='form-grid'>
        <div className='form-container'>
          <Typography variant='h4' color={'#202020'}>LogIn Your Account</Typography>
          <div><Typography className='text' variant='subtitle2'>
            Don't have an account?&nbsp;
            <Link href={CONST_ROUTE_PATH.SIGN_UP} className='link' title='Click here to sign up' >Sign Up</Link>
          </Typography></div>

          {/* Google sign In */}
          {process.env?.REACT_APP_DISABLE_GOOGLE_SIGNIN !== 'true' && (
            <>
              <GoogleSignUp
                handleSuccess={googleSuccessHandle}
                handleFailure={googleFailureHandle}
              />

              {/* Show API error message for Google sign in */}
              {apiErrorMessageForGoogle.length > 0 && (
                <Typography color={'#D32F2F'}>
                  {apiErrorMessageForGoogle}
                </Typography>
              )}

              <Divider style={{ width: '100%', color: '#84818A' }}>or</Divider>
            </>
          )}

          {/* Manual Sign In */}
          <Box component="form" noValidate onSubmit={handleSubmit}>
            <TextField
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus
              type='text'
              error={isInvalidEmail}
              helperText={errors.email}
              onChange={handleChange}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type={showPassword ? 'text' : 'password'}
              id="password"
              autoComplete="current-password"
              error={isInvalidPassword}
              placeholder='must have 6 characters'
              helperText={errors.password}
              onChange={handleChange}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      onClick={() => setShowPassword((show) => !show)}
                      onMouseDown={(e) => e.preventDefault()}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {/* CAPTCHA */}
            <Captcha ref={captchaRef} onVerify={handleCaptchaVerify} />
            {captchaError.length > 0 && (
              <Typography className='signin-captcha-error' color={'#D32F2F'} variant='caption'>
                {captchaError}
              </Typography>
            )}

            {/* // Show API error message */}
            {apiErrorMessage.length > 0 && (
              <Typography color={'#D32F2F'}>{apiErrorMessage}</Typography>
            )}

            {/* Sign In Button */}
            <LoadingButton
              // onClick={() => validateOnboarding()}
              className='submit-btn'
              loading={inProgress}
              loadingPosition="end"
              variant="contained"
              fullWidth
              type='submit'
              disabled={isButtonDisabled}
              endIcon={''}
            >
              Sign In
            </LoadingButton>

            <div className='forget-password-div'>
              <Typography className='text' variant='caption'>Forgot Passward?&nbsp;
                <Link href={CONST_ROUTE_PATH.FORGOT_PASSWORD} className='link' title='Click here for forget password'>
                  Click Here
                </Link>
              </Typography>
            </div>
          </Box>
        </div>
      </Grid>
    </Grid>
  );
}